import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Layers } from 'lucide-react'
import { ROUTE_ORDERS } from '@klear/constants'
import { operationalAmountLabel } from '@klear/operational/presentation'
import type { Payable } from '@klear/payable/payable'
import { isEventLine, isNavigableLine, linkedEventIdsFrom } from '@klear/payment/line-item'
import { useOperationals } from '@klear/ui/hooks/use-operationals'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@klear/ui/components/hover-card'
import { coveredEventDetails, coveredLineMeta } from '@/app/payable/workflow/payable-covered-lines'

type PayableLinkedEventsProps = {
  payable: Payable
  urlPrefixApp: string
}

export function PayableLinkedEvents({ payable, urlPrefixApp }: PayableLinkedEventsProps) {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const lines = payable.lineItems
  const eventCount = lines.filter(isEventLine).length

  const coveredIds = useMemo(() => linkedEventIdsFrom(lines), [lines])
  const { operationals } = useOperationals({ orderEventId: coveredIds }, { enabled: isOpen && coveredIds.length > 0 })
  const detailByEventId = useMemo(() => coveredEventDetails(operationals), [operationals])

  if (lines.length < 2) {
    return <div className="text-right font-semibold">{payable.amount.toString()}</div>
  }

  const summary =
    lines.length > eventCount
      ? `covers ${eventCount} operational ${eventCount === 1 ? 'event' : 'events'} and ${lines.length - eventCount} line item${lines.length - eventCount === 1 ? '' : 's'}`
      : `covers ${eventCount} operational events`

  return (
    <HoverCard openDelay={120} onOpenChange={setIsOpen}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="ml-auto flex items-center gap-1.5 rounded font-semibold underline decoration-dotted decoration-from-font underline-offset-4 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={`${payable.amount.toString()} — ${summary}`}
        >
          <Layers className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          {payable.amount.toString()}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-96 p-0">
        <div className="border-b border-border px-3 py-2">
          <p className="text-sm font-medium text-foreground">
            {summary.charAt(0).toUpperCase()}
            {summary.slice(1)}
          </p>
          <p className="text-xs text-muted-foreground">This payment&apos;s amount is their sum.</p>
        </div>
        <ul className="max-h-64 overflow-y-auto py-1">
          {lines.map((item, index) => {
            const meta = coveredLineMeta(item, detailByEventId)
            const label = (
              <>
                <span className="min-w-0">
                  <span className="block truncate">{item.label}</span>
                  {meta.length > 0 ? (
                    <span className="block tabular-nums text-xs text-muted-foreground">{meta.join(' · ')}</span>
                  ) : null}
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {operationalAmountLabel(item.amount)}
                </span>
              </>
            )
            return (
              <li key={item.orderEventId ?? `${item.source}-${index}`}>
                {isNavigableLine(item) ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`${urlPrefixApp}/${ROUTE_ORDERS}/${item.orderId}?orderEventId=${item.orderEventId}`)
                    }}
                    className="flex w-full items-start justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                  >
                    {label}
                  </button>
                ) : (
                  <div className="flex w-full items-start justify-between gap-3 px-3 py-1.5 text-left text-sm">
                    {label}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </HoverCardContent>
    </HoverCard>
  )
}
